import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import useScrollToTop from '../hooks/useScrollToTop';
import SEOHead from '../components/seo/SEOHead';
import blogPosts from '../data/blog-posts.json';

const Blog = () => {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="CustomSync Blog - E-commerce Automation Insights"
        description="Discover insights, tips, and updates about e-commerce automation and order management. Learn how CustomSync streamlines Amazon customization data flow to ShipStation and Veeqo."
        keywords="e-commerce blog, Amazon seller tips, ShipStation tips, Veeqo tips, customization automation, order management blog"
        type="website"
        url="https://customsync.app/blog"
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
                CustomSync Blog
              </h1>
              <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                Insights, tips, and updates about e-commerce automation and order management
              </p>
            </div>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts
              .filter(post => post.published !== false)
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((post) => (
              <article key={post.slug} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                {post.image && (
                  <div className="aspect-w-16 aspect-h-9">
                    <Link to={`/blog/${post.slug}`}>
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-48 object-cover hover:opacity-90 transition-opacity duration-300"
                      />
                    </Link>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <time dateTime={post.date}>{post.date}</time>
                    {post.category && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                          {post.category}
                        </span>
                      </>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                    <Link to={`/blog/${post.slug}`} className="hover:text-blue-600 transition-colors">
                      {post.title}
                    </Link>
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Read more
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
